/**
 * Brand logo for Trading Journal / Wealth At Any Age.
 *
 * <Logo />              — square icon only (default 36px)
 * <Logo size={48} />    — icon at custom size
 * <Logo wordmark />     — icon + "Trading Journal" text beside it
 * <Logo wordmark light />  — light text variant for dark backgrounds
 */

export default function Logo({ size = 36, wordmark = false, light = true, className = '' }) {
  const id = `logo-grad-${size}`; // unique gradient ID per size to avoid SVG conflicts

  const icon = (
    <svg
      width={size}
      height={size}
      viewBox="0 0 44 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={wordmark ? '' : className}
      aria-label="Trading Journal logo"
    >
      <defs>
        {/* Line gradient: emerald → amber */}
        <linearGradient id={`${id}-line`} x1="6" y1="38" x2="38" y2="6" gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="#10B981" />
          <stop offset="100%" stopColor="#F59E0B" />
        </linearGradient>

        {/* Area fill under the curve */}
        <linearGradient id={`${id}-area`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#F59E0B" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#10B981" stopOpacity="0.02" />
        </linearGradient>

        {/* Clip to rounded square */}
        <clipPath id={`${id}-clip`}>
          <rect width="44" height="44" rx="10" />
        </clipPath>
      </defs>

      {/* Background */}
      <rect width="44" height="44" rx="10" fill="#0D1B2E" />

      {/* Subtle grid lines — horizontal only, very faint */}
      <g clipPath={`url(#${id}-clip)`} opacity="0.06">
        {[12, 22, 32].map(y => (
          <line key={y} x1="4" y1={y} x2="40" y2={y} stroke="#94A3B8" strokeWidth="0.5" />
        ))}
      </g>

      {/* Area fill under the compound-growth curve */}
      <path
        d="M7 37 C10 34 14 30 18 25 C22 20 27 15 37 7 L37 37 Z"
        fill={`url(#${id}-area)`}
        clipPath={`url(#${id}-clip)`}
      />

      {/* The main wealth curve — compound growth hockey stick */}
      <path
        d="M7 37 C10 34 14 30 18 25 C22 20 27 15 37 7"
        stroke={`url(#${id}-line)`}
        strokeWidth="2.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />

      {/* Terminal dot at peak — amber glow */}
      <circle cx="37" cy="7" r="4"   fill="#F59E0B" opacity="0.25" />
      <circle cx="37" cy="7" r="2.5" fill="#F59E0B" />
      <circle cx="37" cy="7" r="1.2" fill="white"   opacity="0.9" />

      {/* Base dot — emerald start */}
      <circle cx="7"  cy="37" r="2" fill="#10B981" opacity="0.6" />
    </svg>
  );

  if (!wordmark) return icon;

  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      {icon}
      <div className="flex flex-col leading-none">
        <span
          style={{ color: light ? '#FFFFFF' : '#0D1B2E', fontWeight: 700, fontSize: size * 0.42, letterSpacing: '-0.01em' }}
        >
          Trading Journal
        </span>
        <span
          style={{ color: '#F59E0B', fontWeight: 500, fontSize: size * 0.26, letterSpacing: '0.06em', textTransform: 'uppercase', marginTop: 2 }}
        >
          Wealth At Any Age
        </span>
      </div>
    </div>
  );
}
